import type {
  GeoLocation,
  WeatherForecast,
  WeatherProvider,
  HourlyWeatherPoint,
  WeatherConditionCode,
  DayWeatherSummary,
  WeatherPeriodSummary,
  WeatherTransition,
  WeatherProvenance,
} from '@yada/types';
import { toLocalDate } from '@yada/types';

// Coordinate precision: Round geographic coordinates to 4 decimal places (~11m grid on Earth)
// as recommended for upstream provider caching and location privacy.
export function sanitizeCoordinates(lat: number, lon: number): { lat: number; lon: number } {
  return {
    lat: Math.round(lat * 10000) / 10000,
    lon: Math.round(lon * 10000) / 10000,
  };
}

/**
 * Maps MET Norway symbol_code string to YADA normalized WeatherConditionCode
 * MET symbol codes e.g. 'clearsky_day', 'fair_night', 'heavyrainshowers_polartwilight', etc.
 */
export function mapMetSymbolCodeToCondition(symbolCode: string): {
  condition: WeatherConditionCode;
  description: string;
} {
  const code = (symbolCode || '').toLowerCase().split('_')[0];
  const isNight = (symbolCode || '').toLowerCase().includes('night');

  switch (code) {
    case 'clearsky':
      return {
        condition: isNight ? 'clear_night' : 'clear_day',
        description: isNight ? 'Clear night' : 'Clear sky',
      };
    case 'fair':
    case 'partlycloudy':
      return {
        condition: isNight ? 'partly_cloudy_night' : 'partly_cloudy_day',
        description: 'Partly cloudy',
      };
    case 'cloudy':
      return { condition: 'cloudy', description: 'Cloudy' };
    case 'fog':
      return { condition: 'fog', description: 'Fog' };
    case 'lightrainshowers':
      return {
        condition: isNight ? 'light_rain_showers_night' : 'light_rain_showers_day',
        description: 'Light rain showers',
      };
    case 'rainshowers':
      return {
        condition: isNight ? 'rain_showers_night' : 'rain_showers_day',
        description: 'Rain showers',
      };
    case 'heavyrainshowers':
      return {
        condition: isNight ? 'heavy_rain_showers_night' : 'heavy_rain_showers_day',
        description: 'Heavy rain showers',
      };
    case 'lightrain':
      return { condition: 'light_rain', description: 'Light rain' };
    case 'rain':
      return { condition: 'rain', description: 'Rain' };
    case 'heavyrain':
      return { condition: 'heavy_rain', description: 'Heavy rain' };
    case 'lightsleetshowers':
    case 'sleetshowers':
      return {
        condition: isNight ? 'sleet_showers_night' : 'sleet_showers_day',
        description: 'Sleet showers',
      };
    case 'lightsleet':
    case 'sleet':
      return { condition: 'sleet', description: 'Sleet' };
    case 'heavysleet':
    case 'heavysleetshowers':
      return { condition: 'heavy_sleet', description: 'Heavy sleet' };
    case 'lightsnowshowers':
    case 'snowshowers':
      return {
        condition: isNight ? 'snow_showers_night' : 'snow_showers_day',
        description: 'Snow showers',
      };
    case 'lightsnow':
      return { condition: 'light_snow', description: 'Light snow' };
    case 'snow':
      return { condition: 'snow', description: 'Snow' };
    case 'heavysnow':
    case 'heavysnowshowers':
      return { condition: 'heavy_snow', description: 'Heavy snow' };
    case 'lightrainandthunder':
    case 'rainandthunder':
    case 'heavyrainandthunder':
    case 'lightrainshowersandthunder':
    case 'rainshowersandthunder':
    case 'heavyrainshowersandthunder':
      return { condition: 'rain_and_thunder', description: 'Rain and thunder' };
    case 'lightsleetandthunder':
    case 'sleetandthunder':
    case 'heavysleetandthunder':
      return { condition: 'sleet_and_thunder', description: 'Sleet and thunder' };
    case 'lightsnowandthunder':
    case 'snowandthunder':
    case 'heavysnowandthunder':
      return { condition: 'snow_and_thunder', description: 'Snow and thunder' };
    default:
      if (symbolCode.includes('thunder')) {
        return { condition: 'thunderstorm', description: 'Thunderstorm' };
      }
      if (symbolCode.includes('snow')) {
        return { condition: 'snow', description: 'Snow' };
      }
      if (symbolCode.includes('rain')) {
        return { condition: 'rain', description: 'Rain' };
      }
      if (symbolCode.includes('sleet')) {
        return { condition: 'sleet', description: 'Sleet' };
      }
      return { condition: 'unknown', description: 'Clear' };
  }
}

export function mapWeatherConditionCodeToDescription(condition: WeatherConditionCode): string {
  switch (condition) {
    case 'clear_night':
      return 'Clear night';
    case 'clear_day':
      return 'Clear sky';
    case 'partly_cloudy_night':
    case 'partly_cloudy_day':
      return 'Partly cloudy';
    case 'cloudy':
      return 'Cloudy';
    case 'fog':
      return 'Fog';
    case 'light_rain':
    case 'light_rain_showers_day':
    case 'light_rain_showers_night':
      return 'Light rain';
    case 'rain':
    case 'rain_showers_day':
    case 'rain_showers_night':
      return 'Rain';
    case 'heavy_rain':
    case 'heavy_rain_showers_day':
    case 'heavy_rain_showers_night':
      return 'Heavy rain';
    case 'light_sleet':
    case 'sleet':
    case 'sleet_showers_day':
    case 'sleet_showers_night':
      return 'Sleet';
    case 'heavy_sleet':
      return 'Heavy sleet';
    case 'light_snow':
    case 'snow':
    case 'snow_showers_day':
    case 'snow_showers_night':
      return 'Snow';
    case 'heavy_snow':
      return 'Heavy snow';
    case 'thunderstorm':
    case 'rain_and_thunder':
    case 'sleet_and_thunder':
    case 'snow_and_thunder':
      return 'Thunderstorm';
    case 'unknown':
    default:
      return 'Clear';
  }
}

/**
 * Calculates apparent / feels-like temperature using wind chill (for <=10°C with wind)
 * or Australian apparent temperature equation combining temp, humidity, and wind.
 */
export function computeApparentTemperature(tempC: number, windSpeedMps: number = 0, humidityPct: number = 50): number {
  const v = windSpeedMps;
  // Vapor pressure in hPa
  const e = (humidityPct / 100) * 6.105 * Math.exp((17.27 * tempC) / (237.7 + tempC));
  // Australian Bureau of Meteorology Apparent Temperature formula:
  // AT = Ta + 0.33×e - 0.70×ws - 4.00
  const at = tempC + 0.33 * e - 0.7 * v - 4.0;
  return Math.round(at * 10) / 10;
}

export const MET_NORWAY_PROVENANCE: WeatherProvenance = {
  provider: 'met-norway',
  attribution: 'Weather forecast from MET Norway',
  attributionUrl: 'https://www.met.no/',
  license: 'NLOD / CC BY 4.0',
  valueKind: 'forecast',
};

export class MetNorwayProvider implements WeatherProvider {
  readonly id = 'met-norway';
  readonly name = 'MET Norway';
  readonly provenance = MET_NORWAY_PROVENANCE;

  private userAgent: string;

  constructor(userAgent = 'YADA/1.0 (https://yada.tools; contact@yada.tools)') {
    this.userAgent = userAgent;
  }

  async fetchForecast(location: GeoLocation): Promise<WeatherForecast> {
    const { lat, lon } = sanitizeCoordinates(location.latitude, location.longitude);
    const altitude = location.altitudeMeters !== undefined ? Math.round(location.altitudeMeters) : undefined;

    let url = `https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${lat}&lon=${lon}`;
    if (altitude !== undefined) {
      url += `&altitude=${altitude}`;
    }

    const headers: Record<string, string> = {
      'User-Agent': this.userAgent,
      'Accept': 'application/json',
    };

    const res = await fetch(url, { headers });
    if (!res.ok) {
      throw new Error(`MET Norway request failed with status ${res.status}: ${res.statusText}`);
    }

    const json = await res.json();
    return this.parseMetForecast(json, location, res.headers.get('expires'));
  }

  private parseMetForecast(json: any, location: GeoLocation, expiresHeader: string | null): WeatherForecast {
    const timeseries: any[] = json?.properties?.timeseries ?? [];
    if (timeseries.length === 0) {
      throw new Error('Empty timeseries returned by MET Norway');
    }

    const updatedAt = json?.properties?.meta?.updated_at || new Date().toISOString();

    const provenance: WeatherProvenance = {
      ...this.provenance,
      updatedAt,
    };

    const hourly: HourlyWeatherPoint[] = timeseries.map((entry: any) => {
      const time = entry.time;
      const details = entry.data?.instant?.details ?? {};
      const next1h = entry.data?.next_1_hours;
      const next6h = entry.data?.next_6_hours;
      const next12h = entry.data?.next_12_hours;

      const symbolCode =
        next1h?.summary?.symbol_code ||
        next6h?.summary?.symbol_code ||
        next12h?.summary?.symbol_code ||
        'cloudy';

      const { condition, description } = mapMetSymbolCodeToCondition(symbolCode);

      const temperature = details.air_temperature ?? 0;
      const windSpeedMps = details.wind_speed ?? 0;
      const windGustMps = details.wind_speed_of_gust;
      const windDirectionDegrees = details.wind_from_direction;
      const relativeHumidityPercent = details.relative_humidity;
      const cloudCoverPercent = details.cloud_area_fraction;
      const pressureHpa = details.air_pressure_at_sea_level;
      const uvIndex = details.ultraviolet_index_clear_sky;

      const precipitationAmountMm =
        next1h?.details?.precipitation_amount ??
        (next6h?.details?.precipitation_amount !== undefined ? next6h.details.precipitation_amount / 6 : 0);

      const precipitationProbability =
        next1h?.details?.probability_of_precipitation ??
        next6h?.details?.probability_of_precipitation;

      const apparentTemperature = computeApparentTemperature(
        temperature,
        windSpeedMps,
        relativeHumidityPercent ?? 50
      );

      return {
        time,
        temperature,
        apparentTemperature,
        condition,
        conditionDescription: description,
        precipitationAmountMm,
        precipitationProbability,
        rainAmountMm: condition.includes('rain') ? precipitationAmountMm : 0,
        snowfallAmountMm: condition.includes('snow') ? precipitationAmountMm : 0,
        cloudCoverPercent,
        windSpeedMps,
        windGustMps,
        windDirectionDegrees,
        relativeHumidityPercent,
        uvIndex,
        pressureHpa,
      };
    });

    const current = hourly[0];

    // Compute daily summaries from hourly data
    const daily = deriveDailySummaries(hourly, provenance);

    const now = new Date();
    const cachedAt = now.toISOString();
    // Default TTL: 1 hour if no expires header
    let expiresAt = new Date(now.getTime() + 60 * 60 * 1000).toISOString();
    if (expiresHeader) {
      try {
        const parsed = new Date(expiresHeader);
        if (!isNaN(parsed.getTime())) {
          expiresAt = parsed.toISOString();
        }
      } catch {}
    }

    return {
      location,
      provenance,
      current,
      hourly,
      daily,
      cachedAt,
      expiresAt,
    };
  }
}

function formatLocalDate(d: Date): string {
  return toLocalDate(d);
}

/**
 * Groups hourly points by local date and derives day weather summaries,
 * transitions, collapsed periods, and environmental hints.
 */
export function deriveDailySummaries(
  hourly: HourlyWeatherPoint[],
  provenance: WeatherProvenance
): DayWeatherSummary[] {
  const byDate = new Map<string, HourlyWeatherPoint[]>();

  for (const pt of hourly) {
    const dateStr = formatLocalDate(new Date(pt.time));
    if (!byDate.has(dateStr)) {
      byDate.set(dateStr, []);
    }
    byDate.get(dateStr)!.push(pt);
  }

  const result: DayWeatherSummary[] = [];

  for (const [dateStr, points] of byDate.entries()) {
    if (points.length === 0) continue;

    let minTemp = Infinity;
    let maxTemp = -Infinity;
    let totalPrecipitationMm = 0;
    let maxPrecipitationProbability = 0;
    let maxWindSpeed = 0;
    let maxUv = 0;

    const conditionCounts = new Map<WeatherConditionCode, number>();

    for (const p of points) {
      if (p.temperature < minTemp) minTemp = p.temperature;
      if (p.temperature > maxTemp) maxTemp = p.temperature;
      if (p.precipitationAmountMm) totalPrecipitationMm += p.precipitationAmountMm;
      if (p.precipitationProbability && p.precipitationProbability > maxPrecipitationProbability) {
        maxPrecipitationProbability = p.precipitationProbability;
      }
      if (p.windSpeedMps && p.windSpeedMps > maxWindSpeed) maxWindSpeed = p.windSpeedMps;
      if (p.uvIndex && p.uvIndex > maxUv) maxUv = p.uvIndex;

      conditionCounts.set(p.condition, (conditionCounts.get(p.condition) || 0) + 1);
    }

    totalPrecipitationMm = Math.round(totalPrecipitationMm * 10) / 10;
    minTemp = Math.round(minTemp);
    maxTemp = Math.round(maxTemp);

    // Find dominant condition
    let dominantCondition: WeatherConditionCode = 'cloudy';
    let maxCount = -1;
    for (const [cond, count] of conditionCounts.entries()) {
      if (count > maxCount) {
        maxCount = count;
        dominantCondition = cond;
      }
    }

    const currentTemp = points[0]?.temperature !== undefined ? Math.round(points[0].temperature) : undefined;
    const currentCondition = points[0]?.condition;
    const dominantDesc = mapWeatherConditionCodeToDescription(dominantCondition);

    // Meaningful transitions detection
    const transitions = detectTransitions(points);

    // Collapsed periods (morning, afternoon, evening, night)
    const periods = derivePeriods(points);

    // Headline & shape narrative
    const { headline, shapeDescription } = generateDayNarrative(
      dateStr,
      currentTemp,
      minTemp,
      maxTemp,
      periods,
      transitions,
      dominantDesc
    );

    // Environmental context for meals, home, calendar
    const isRainy = totalPrecipitationMm >= 1.0 || maxPrecipitationProbability >= 60;
    const eveningPoints = points.filter(p => {
      const h = new Date(p.time).getUTCHours();
      return h >= 17 && h <= 23;
    });
    const avgEveningTemp =
      eveningPoints.length > 0
        ? eveningPoints.reduce((s, p) => s + p.temperature, 0) / eveningPoints.length
        : minTemp;

    const isColdEvening = avgEveningTemp <= 10;
    const isRainyEvening = eveningPoints.some(
      p => (p.precipitationAmountMm && p.precipitationAmountMm > 0.5) || p.condition.includes('rain')
    );
    const isHotDay = maxTemp >= 28;
    const isWarmSunnyAfternoon =
      maxTemp >= 20 &&
      periods.some(
        p => (p.name === 'afternoon' || p.name === 'midday') && (p.condition === 'clear_day' || p.condition === 'partly_cloudy_day')
      );
    const majorTempDrop = maxTemp - minTemp >= 10;

    const actionableHints = {
      takeUmbrella: isRainy || transitions.some(t => t.type === 'rain_start' || t.type === 'snow_start'),
      dressWarm: minTemp <= 8 || avgEveningTemp <= 10,
      dressCool: maxTemp >= 24,
      heavyWindWarning: maxWindSpeed >= 12,
      uvProtection: maxUv >= 5,
    };

    const environmentalContext = {
      isColdEvening,
      isHotDay,
      isWarmSunnyAfternoon,
      isRainyEvening,
      majorTempDrop,
    };

    result.push({
      date: dateStr,
      provenance,
      minTemp,
      maxTemp,
      currentTemp,
      currentCondition,
      dominantCondition,
      headline,
      shapeDescription,
      transitions,
      periods,
      totalPrecipitationMm,
      maxPrecipitationProbability,
      actionableHints,
      environmentalContext,
    });
  }

  return result;
}

/**
 * Detects significant meteorological transitions across a sequence of hourly points
 */
export function detectTransitions(points: HourlyWeatherPoint[]): WeatherTransition[] {
  const transitions: WeatherTransition[] = [];

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const hourStr = curr.time.slice(11, 16);

    const prevRain = (prev.precipitationAmountMm ?? 0) > 0.1 || prev.condition.includes('rain');
    const currRain = (curr.precipitationAmountMm ?? 0) > 0.1 || curr.condition.includes('rain');

    if (!prevRain && currRain) {
      transitions.push({
        time: curr.time,
        type: 'rain_start',
        summary: `Rain starting ~${hourStr}`,
        significance: (curr.precipitationAmountMm ?? 0) > 1.5 ? 'major' : 'moderate',
      });
    } else if (prevRain && !currRain) {
      transitions.push({
        time: curr.time,
        type: 'rain_end',
        summary: `Rain ending ~${hourStr}`,
        significance: 'moderate',
      });
    }

    const prevSnow = (prev.snowfallAmountMm ?? 0) > 0.1 || prev.condition.includes('snow');
    const currSnow = (curr.snowfallAmountMm ?? 0) > 0.1 || curr.condition.includes('snow');

    if (!prevSnow && currSnow) {
      transitions.push({
        time: curr.time,
        type: 'snow_start',
        summary: `Snow starting ~${hourStr}`,
        significance: 'major',
      });
    } else if (prevSnow && !currSnow) {
      transitions.push({
        time: curr.time,
        type: 'snow_end',
        summary: `Snow stopping ~${hourStr}`,
        significance: 'moderate',
      });
    }

    // Material wind increase (jump of >= 5 m/s reaching >= 10 m/s)
    if (
      curr.windSpeedMps !== undefined &&
      prev.windSpeedMps !== undefined &&
      curr.windSpeedMps - prev.windSpeedMps >= 5 &&
      curr.windSpeedMps >= 10
    ) {
      transitions.push({
        time: curr.time,
        type: 'wind_increase',
        summary: `Wind picking up to ${Math.round(curr.windSpeedMps)} m/s around ${hourStr}`,
        significance: curr.windSpeedMps >= 14 ? 'major' : 'moderate',
      });
    }

    // Substantial temp change over 3 hours
    if (i >= 3) {
      const p3 = points[i - 3];
      const diff = curr.temperature - p3.temperature;
      if (diff >= 6) {
        transitions.push({
          time: curr.time,
          type: 'temp_rise',
          summary: `Warming up to ${Math.round(curr.temperature)}° around ${hourStr}`,
          significance: 'moderate',
        });
      } else if (diff <= -6) {
        transitions.push({
          time: curr.time,
          type: 'temp_drop',
          summary: `Cooling down to ${Math.round(curr.temperature)}° around ${hourStr}`,
          significance: 'moderate',
        });
      }
    }
  }

  return transitions;
}

/**
 * Derives natural periods (morning 06-12, afternoon 12-18, evening 18-23, night 23-06)
 * and collapses periods where conditions are effectively unchanged.
 */
export function derivePeriods(points: HourlyWeatherPoint[]): WeatherPeriodSummary[] {
  const buckets: Record<string, HourlyWeatherPoint[]> = {
    night: [],
    morning: [],
    afternoon: [],
    evening: [],
  };

  for (const pt of points) {
    const h = new Date(pt.time).getUTCHours();
    if (h >= 6 && h < 12) buckets.morning.push(pt);
    else if (h >= 12 && h < 18) buckets.afternoon.push(pt);
    else if (h >= 18 && h < 23) buckets.evening.push(pt);
    else buckets.night.push(pt);
  }

  const periodDefs = [
    { key: 'morning', label: 'Morning' },
    { key: 'afternoon', label: 'Afternoon' },
    { key: 'evening', label: 'Evening' },
    { key: 'night', label: 'Night' },
  ];

  const rawPeriods: WeatherPeriodSummary[] = [];

  for (const { key } of periodDefs) {
    const pts = buckets[key];
    if (pts.length === 0) continue;

    let minTemp = Infinity;
    let maxTemp = -Infinity;
    let totalPrecip = 0;
    let maxProb = 0;
    let maxWind = 0;
    const condCounts = new Map<WeatherConditionCode, number>();

    for (const p of pts) {
      if (p.temperature < minTemp) minTemp = p.temperature;
      if (p.temperature > maxTemp) maxTemp = p.temperature;
      if (p.precipitationAmountMm) totalPrecip += p.precipitationAmountMm;
      if (p.precipitationProbability && p.precipitationProbability > maxProb) maxProb = p.precipitationProbability;
      if (p.windSpeedMps && p.windSpeedMps > maxWind) maxWind = p.windSpeedMps;
      condCounts.set(p.condition, (condCounts.get(p.condition) || 0) + 1);
    }

    let dominant: WeatherConditionCode = pts[0].condition;
    let maxC = -1;
    for (const [c, count] of condCounts.entries()) {
      if (count > maxC) {
        maxC = count;
        dominant = c;
      }
    }

    const { description } = mapMetSymbolCodeToCondition(dominant);
    const minT = Math.round(minTemp);
    const maxT = Math.round(maxTemp);
    const precip = Math.round(totalPrecip * 10) / 10;

    let summary = `${description}, ${minT === maxT ? `${minT}°` : `${minT}° to ${maxT}°`}`;
    if (precip > 0.5) {
      summary += ` with ${precip}mm rain`;
    }

    rawPeriods.push({
      name: key,
      startTime: pts[0].time,
      endTime: pts[pts.length - 1].time,
      condition: dominant,
      conditionDescription: description,
      minTemp: minT,
      maxTemp: maxT,
      precipitationTotalMm: precip,
      precipitationProbabilityMax: maxProb,
      maxWindSpeedMps: maxWind ? Math.round(maxWind) : undefined,
      summary,
    });
  }

  return rawPeriods;
}

/**
 * Builds the shape headline and summary description
 * e.g. "9° now → 18° afternoon"
 * "Dry morning · rain from ~15:00 · 11° late evening"
 */
export function generateDayNarrative(
  dateStr: string,
  currentTemp: number | undefined,
  minTemp: number,
  maxTemp: number,
  periods: WeatherPeriodSummary[],
  transitions: WeatherTransition[],
  dominantConditionDescription?: string
): { headline: string; shapeDescription: string } {
  const afternoonPeriod = periods.find(p => p.name === 'afternoon');
  const morningPeriod = periods.find(p => p.name === 'morning');
  const eveningPeriod = periods.find(p => p.name === 'evening');
  const nightPeriod = periods.find(p => p.name === 'night');

  let headline = '';
  if (currentTemp !== undefined) {
    if (afternoonPeriod && afternoonPeriod.maxTemp > currentTemp) {
      headline = `${currentTemp}° now → ${afternoonPeriod.maxTemp}° afternoon`;
    } else if (maxTemp > currentTemp) {
      headline = `${currentTemp}° now → ${maxTemp}° max`;
    } else if (minTemp < currentTemp) {
      if (eveningPeriod && eveningPeriod.minTemp < currentTemp) {
        headline = `${currentTemp}° now → ${eveningPeriod.minTemp}° evening`;
      } else if (nightPeriod && nightPeriod.minTemp < currentTemp) {
        headline = `${currentTemp}° now → ${nightPeriod.minTemp}° tonight`;
      } else {
        headline = `${currentTemp}° now → dropping to ${minTemp}°`;
      }
    } else if (minTemp === maxTemp) {
      if (dominantConditionDescription) {
        if (nightPeriod || eveningPeriod) {
          headline =
            dominantConditionDescription.toLowerCase().includes('night') ||
            dominantConditionDescription.toLowerCase().includes('clear')
              ? dominantConditionDescription
              : `${dominantConditionDescription} tonight`;
        } else {
          headline = dominantConditionDescription;
        }
      } else {
        headline = `${currentTemp}° now`;
      }
    } else {
      headline = `${currentTemp}° now · ${minTemp}° / ${maxTemp}°`;
    }
  } else {
    headline = minTemp === maxTemp ? `${minTemp}°` : `${minTemp}° to ${maxTemp}°`;
  }

  // Narrative shape
  const parts: string[] = [];

  if (morningPeriod) {
    const isDryMorning = morningPeriod.precipitationTotalMm === 0 && morningPeriod.precipitationProbabilityMax < 30;
    parts.push(isDryMorning ? 'Dry morning' : morningPeriod.conditionDescription.toLowerCase());
  }

  const rainTransition = transitions.find(t => t.type === 'rain_start' || t.type === 'snow_start');
  if (rainTransition) {
    parts.push(rainTransition.summary.toLowerCase());
  } else if (afternoonPeriod) {
    if (afternoonPeriod.precipitationTotalMm > 0.5) {
      parts.push(`rain in the afternoon`);
    } else if (afternoonPeriod.condition === 'clear_day') {
      parts.push('sunny afternoon');
    }
  }

  if (eveningPeriod && (morningPeriod || afternoonPeriod)) {
    parts.push(`${eveningPeriod.minTemp}° evening`);
  }

  let shapeDescription = '';
  if (parts.length > 0) {
    shapeDescription = parts.join(' · ');
  } else {
    if (dominantConditionDescription && !headline.startsWith(dominantConditionDescription)) {
      shapeDescription = dominantConditionDescription;
    } else if (minTemp !== maxTemp) {
      shapeDescription = `${minTemp}° to ${maxTemp}°`;
    } else {
      shapeDescription = '';
    }
  }

  return { headline, shapeDescription };
}
